"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PERIPHERAL_BLOCKS = exports.TerminalPeripheralConfigOverride = exports.TerminalPeripheralConfig = exports.WorkDirectoryPeripheralConfig = exports.FISCAL_SECURITY_MODULE_WORK_DIRECTORY_MAX = exports.BarcodeScannerConfig = exports.ScannerGroupSeparator = exports.SCANNER_GROUP_SEPARATOR_SUBSTITUTES = exports.LabelPrinterConfig = exports.ReceiptPrinterConfig = exports.ReceiptPrinterOwner = exports.ReceiptPrinterLogoKeycode = exports.BaggingScaleConfig = exports.BaggingScaleDivisionGrams = exports.BaggingScaleModel = exports.PaymentTerminalConfig = exports.PaymentTerminalDriver = exports.ManagedEndpointPeripheralConfig = exports.NetworkPeripheralConfig = exports.SerialPeripheralConfig = exports.ReceiptPrinterFont = exports.ReceiptPrinterFeedLinesBeforeCut = exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_DEFAULT = exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MAX = exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MIN = exports.ReceiptPrinterCut = exports.ReceiptPrinterCodepageTable = exports.ReceiptPrinterCodepage = exports.PeripheralTransport = void 0;
const zod_1 = require("zod");
const softfiscal_config_js_1 = require("./softfiscal-config.js");
/**
 * BOS-authored peripheral configuration for one terminal (RFL-238 / RFL-240).
 *
 * ------------------------------------------------------------------
 * THIS SHAPE IS NOT OURS TO INVENT
 * ------------------------------------------------------------------
 * The terminal half shipped first (RFL-243 / RFL-244 / RFL-337 / RFL-338). Its
 * parser — `reform_lite_contracts/lib/src/terminal_peripheral_config.dart` —
 * already reads exactly these keys off `GET /sync/terminal-config`, and has
 * been falling back to build defines because Back Office never sent them.
 * Every name and nesting level below mirrors that file. Renaming anything here
 * silently un-authors a peripheral on every till in the fleet, because the
 * terminal treats a key it does not recognise as "not authored" rather than as
 * an error.
 *
 * ------------------------------------------------------------------
 * ABSENCE MEANS "NOT AUTHORED"
 * ------------------------------------------------------------------
 * Never "off", never "the default". A terminal that meets a missing block
 * falls back to its build-time `--dart-define`, then to an OS-aware default.
 * A partially authored config is the normal case: a store may set one lane's
 * scanner port and nothing else. That is why every field is optional and why
 * empty blocks are dropped on the wire rather than sent as `{}`.
 *
 * ------------------------------------------------------------------
 * WHY IT IS VALIDATED HERE
 * ------------------------------------------------------------------
 * Same reason as `terminal-config.schema.ts`: the till fails closed on
 * malformed config, so an unchecked typo authored in Back Office becomes a
 * lane that will not sell, discovered at the lane. `.strict()` throughout — an
 * unknown key is a misspelled one, and keeping it would show the admin a
 * setting the terminal ignores.
 */
/** How a peripheral is attached. Mirrors the Dart `PeripheralTransport`. */
exports.PeripheralTransport = zod_1.z.enum([
    'serial',
    'keyboardWedge',
    'network',
    'usb',
    'localSocket',
    'service',
]);
/**
 * Character tables a receipt printer can be switched to.
 *
 * Restricted to what `tools/release/build-linux.sh --printer-codepage` accepts,
 * because this is the value that decides whether a Ukrainian receipt prints
 * Cyrillic or a page of `?`. An unconstrained string here would be discovered
 * on paper.
 */
exports.ReceiptPrinterCodepage = zod_1.z.enum(['ascii', 'cp1251', 'cp866', 'cp1257']);
/**
 * The printer's own number for the codepage: the `n` of ESC/POS `ESC t n`
 * (VL-346).
 *
 * `codepage` says how the till ENCODES text; this says how it SWITCHES the
 * printer to the matching page, and only the model decides it. An Epson TM
 * series selects WPC1251 with 46 and PC866 with 17; the Citizen CT-S310II has
 * no number for WPC1251 at all and is set by its memory switch. So a vendor
 * table cannot be inferred from the codepage name, and an operator who knows
 * the model authors it.
 *
 * **Absent is not 0.** Absent keeps the till's built-in choice, which for
 * `cp1251` is to send no `ESC t` at all — what the Citizen lanes need. `0` is a
 * real page (PC437) and would switch a Cyrillic printer to Latin.
 */
exports.ReceiptPrinterCodepageTable = zod_1.z.number().int().min(0).max(255);
/**
 * What the receipt printer does after each document (VL-346): a `full` cut, a
 * `partial` cut that leaves a tab, or `none` for a printer without a cutter
 * (the till still feeds the paper past the tear bar).
 *
 * **Absent is a full cut** — what every till printed with before this
 * existed — and the till treats a value it does not recognise the same way.
 */
exports.ReceiptPrinterCut = zod_1.z.enum(['full', 'partial', 'none']);
/** Fewest blank lines the till may be told to feed before the cut (RL-889). */
exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MIN = 0;
/**
 * Most blank lines the till may be told to feed before the cut (RL-889):
 * about 42 mm at the printer's default 1/6 in line spacing, past the cutter
 * of any receipt printer in the estate. More only wastes paper.
 */
exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MAX = 10;
/**
 * What the till feeds when `feedLinesBeforeCut` is absent (RL-889) — the fixed
 * value every till fed before the field existed. Documented here, never
 * written into a config: absent must stay absent.
 */
exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_DEFAULT = 3;
/**
 * Blank lines the receipt printer feeds after the last printed line and before
 * the cut (RL-889), so the blade falls below the last line instead of through
 * it. It belongs to the printer model: how far its cutter sits below the print
 * head. The till feeds it on every document it prints, the register's own
 * X/Z and fiscal documents included, and also with `cut: none` (where it
 * carries the paper past the tear bar).
 *
 * **Absent is 3**, the till's built-in feed, so a till with nothing authored
 * keeps its byte stream. `0` is a real value (no feed). A whole number from
 * {@link RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MIN} to
 * {@link RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MAX}; the till ignores anything
 * else, with a boot-log warning, and feeds 3.
 */
exports.ReceiptPrinterFeedLinesBeforeCut = zod_1.z
    .number()
    .int()
    .min(exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MIN)
    .max(exports.RECEIPT_PRINTER_FEED_LINES_BEFORE_CUT_MAX);
/**
 * ESC/POS character font (RL-251, terminals-side YUKI-117).
 *
 * `a` is 12x24, `b` is 9x17 — smaller print and a shorter receipt.
 *
 * **Absent is not `a`.** Absent means "keep whatever the printer defaults to",
 * and the terminal must not be told font A was chosen when nobody chose it:
 * the resolution chain is synced config -> build define -> printer default, and
 * sending `a` unasked would silently outrank a lane's `--dart-define`.
 *
 * Deliberately independent of `columns`. The font decides how large a character
 * prints; `columns` decides how many are laid out per line. Font B fits roughly
 * 64 columns on 80 mm paper where font A fits 48 — but a lane that wants
 * smaller print and a lane that wants more characters per line are different
 * requests, and changing one must never change the other.
 */
exports.ReceiptPrinterFont = zod_1.z.enum(['a', 'b']);
/**
 * Whether a cash drawer is wired through the receipt printer's DK port
 * (YUKI-163).
 *
 * **Nothing on the wire can answer this.** The DK port behaves identically
 * with a drawer on the end of it and with nothing at all, so the terminal
 * cannot detect it and an operator has to say. Without an authored value the
 * drawer never opens, which is the defect this pairs with terminal-side.
 *
 * **Absent is a third state, not `false`.** The till resolves synced config ->
 * build define -> off, and an authored value takes precedence over the lane's
 * `--printer-drawer` build flag. So sending `false` for a terminal nobody has
 * configured would silently switch off a lane that was built with the drawer
 * enabled. Leave it absent to mean "nobody has said"; author `false` only to
 * mean "there is deliberately no drawer here".
 */
/** A device path. `COM8`, `/dev/ttyS0`, `/dev/ttyUSB0`, `/dev/usb/lp0`. */
const DevicePath = zod_1.z.string().trim().min(1).max(255);
/** A hostname or address. Not parsed further — a lane may use either. */
const HostName = zod_1.z.string().trim().min(1).max(255);
/** A TCP port. */
const NetworkPort = zod_1.z.number().int().min(1).max(65_535);
/**
 * A line rate. Bounded rather than free, because a mistyped baud is a printer
 * that emits plausible-looking garbage instead of failing.
 */
const BaudRate = zod_1.z.number().int().min(50).max(4_000_000);
/** A serial attachment: where, and how fast. */
exports.SerialPeripheralConfig = zod_1.z
    .object({
    port: DevicePath.optional(),
    baud: BaudRate.optional(),
})
    .strict();
/** A network attachment: host and port. */
exports.NetworkPeripheralConfig = zod_1.z
    .object({
    host: HostName.optional(),
    port: NetworkPort.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.port !== undefined && value.host === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['host'],
            message: 'host is required when a network port is authored',
        });
    }
});
/**
 * Canonical endpoint for catalogue-managed payment and fiscal bindings.
 *
 * Additive to the legacy `pinPad` / `fiscalSidecar` network blocks, which
 * older terminal builds still read. It exists because those two cannot express
 * a serial, USB, local-socket or registered-service endpoint.
 *
 * `address` by transport, as the till reads it for the LT security module's
 * X8 channel (`fiscalSecurityModuleEndpoint`, RL-749): `network` — `host:port`
 * or a bare port on this machine; `serial` — a port name (`COM12`,
 * `/dev/ttyUSB0`); `localSocket` — a Unix-domain socket,
 * `unix:/run/reform-lite/sm-x8.sock` (a bare absolute path is read the same
 * way under this transport).
 */
const managedEndpointFields = {
    transport: exports.PeripheralTransport.optional(),
    address: zod_1.z.string().trim().min(1).max(255).optional(),
    baud: BaudRate.optional(),
};
function refineManagedEndpoint(value, ctx) {
    if (value.transport !== undefined &&
        value.transport !== 'keyboardWedge' &&
        value.address === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['address'],
            message: 'address is required for the selected transport',
        });
    }
    if (value.baud !== undefined && value.transport !== 'serial') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['baud'],
            message: 'baud is only valid for a serial transport',
        });
    }
}
exports.ManagedEndpointPeripheralConfig = zod_1.z
    .object(managedEndpointFields)
    .strict()
    .superRefine(refineManagedEndpoint);
/**
 * Which driver speaks to the card terminal at `paymentTerminal.address`
 * (YUKI-364, terminals-side YUKI-362): a payment binding id.
 *
 * `privatbank-ecr` is the PrivatBank ECR driver every POS build resolves when
 * nothing is authored; `pumb-ssi` is the PUMB terminal's SSI JSON driver,
 * which only builds that offer it can run (today the Yuki POS);
 * `eps-asyncpos` is the EPS asyncPOS attended-ECR driver (today the Sanitex
 * POS) and `eps-softpos-baltics` the EPS SoftPOS app driver (Android POS
 * only, EUR).
 *
 * **Absent keeps the build's driver** — exactly what every till did before
 * this existed. The till passes an authored value through as written and, if
 * the build cannot run it, boots with card payments unavailable rather than
 * fall back to another bank's protocol. So the set is closed here: a typo
 * authored in Back Office would otherwise cost a lane its card tender,
 * discovered at the lane.
 */
exports.PaymentTerminalDriver = zod_1.z.enum([
    'privatbank-ecr',
    'pumb-ssi',
    'eps-asyncpos',
    'eps-softpos-baltics',
]);
/**
 * The card terminal's endpoint: a managed endpoint plus, optionally, the
 * driver that speaks to it (YUKI-364). Only this block names a driver; the
 * other managed endpoints keep the plain shape.
 */
exports.PaymentTerminalConfig = zod_1.z
    .object({ ...managedEndpointFields, driver: exports.PaymentTerminalDriver.optional() })
    .strict()
    .superRefine((value, ctx) => {
    refineManagedEndpoint(value, ctx);
    // The PUMB terminal is reached over the LAN only; the till refuses a COM
    // port for it and leaves the lane without card payments.
    if (value.driver === 'pumb-ssi' &&
        value.transport !== undefined &&
        value.transport !== 'network') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['transport'],
            message: 'the pumb-ssi card terminal is reached over the network',
        });
    }
});
/**
 * The security (bagging) scale driver an SCO lane weighs against (SSC-116).
 *
 * `shekel` is the Shekel security scale on a serial or USB-serial port,
 * `digi` a DIGI scale behind the local ScaleModule socket, and `simulated`
 * the software scale for demo builds. Absent means "not authored". Older
 * tills inferred the driver from the transport (serial/usb: Shekel, network:
 * DIGI); since SSC-116 option C a till reports a fitted scale without a model
 * as unavailable, and the one-off maintenance task
 * `peripherals:bagging-scale-model-backfill` writes the model that inference
 * used to give into existing blocks.
 */
exports.BaggingScaleModel = zod_1.z.enum(['shekel', 'digi', 'simulated']);
/**
 * The scale's declared division `d` in grams (SSC-116, design §5).
 *
 * Positive, and a whole number of milligrams, because the till carries it in
 * milligrams: 0.5 g is legal, 0.0001 g is not. Capped at 1 kg, which no
 * checkout scale declares, so a value typed in the wrong unit is refused.
 */
exports.BaggingScaleDivisionGrams = zod_1.z
    .number()
    .positive()
    .max(1000)
    .refine((grams) => Math.abs(grams * 1000 - Math.round(grams * 1000)) < 1e-6, 'division must be a whole number of milligrams');
/**
 * The SCO bagging-area (security) scale (SSC-116).
 *
 * The managed endpoint (`transport`, `address`, `baud`) plus what only a
 * person can say about the device:
 *
 * * `model` names the driver. A till since SSC-116 option C needs it: a
 *   fitted scale without one is reported as unavailable.
 * * `divisionG` is the declared division, the base of the tolerance floor.
 * * `serialNumber` is the Shekel's own serial number, which the driver
 *   checks before it trusts a reading.
 * * `fitted: false` says **no security scale is fitted** to this lane (the
 *   provisioned `none` state, ADR D6). It is exclusive with every other key.
 *
 * **Why `fitted` and not a transport value `none`.** `PeripheralTransport`
 * is how a device is attached, and it is shared by every managed block: a
 * `none` there would become sayable for the payment terminal and the fiscal
 * service too. "No scale" is a fact about the lane, not an attachment. It
 * also has to stay distinct from both "not authored" (absent: the till keeps
 * its build define) and "cleared" (the `null` override tombstone, which also
 * means "till decides"), so it needs a value of its own. The receipt
 * printer's `cashDrawer` is the precedent: a tri-state boolean where absent
 * is "nobody has said" and `false` is "deliberately none".
 *
 * `fitted: true` asserts a scale is fitted; it outranks a build that says
 * `none`, and with no model or transport the till reports the scale as not
 * configured rather than guessing a driver.
 */
exports.BaggingScaleConfig = zod_1.z
    .object({
    ...managedEndpointFields,
    model: exports.BaggingScaleModel.optional(),
    divisionG: exports.BaggingScaleDivisionGrams.optional(),
    serialNumber: zod_1.z.string().trim().min(1).max(64).optional(),
    fitted: zod_1.z.boolean().optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.fitted === false) {
        for (const key of Object.keys(value)) {
            if (key === 'fitted')
                continue;
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [key],
                message: 'a lane with no scale fitted takes no other scale setting',
            });
        }
        return;
    }
    refineManagedEndpoint(value, ctx);
    const { model, transport } = value;
    if (model === undefined &&
        transport === undefined &&
        (value.divisionG !== undefined || value.serialNumber !== undefined)) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['model'],
            message: 'name the scale model or its transport',
        });
    }
    if (model === 'simulated') {
        for (const key of ['transport', 'address', 'baud', 'serialNumber']) {
            if (value[key] === undefined)
                continue;
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: [key],
                message: 'a simulated scale has no device settings',
            });
        }
    }
    if (model === 'shekel') {
        if (transport !== undefined && transport !== 'serial' && transport !== 'usb') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['transport'],
                message: 'a Shekel scale is attached over serial or USB',
            });
        }
        if (value.address === undefined) {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['address'],
                message: 'a Shekel scale needs its serial port',
            });
        }
    }
    if (model === 'digi' && transport !== undefined && transport !== 'network') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['transport'],
            message: 'a DIGI scale is reached over the network',
        });
    }
    const driver = model ??
        (transport === 'network'
            ? 'digi'
            : transport === 'serial' || transport === 'usb'
                ? 'shekel'
                : undefined);
    if (value.serialNumber !== undefined && driver !== undefined && driver !== 'shekel') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['serialNumber'],
            message: 'only a Shekel scale checks its serial number',
        });
    }
});
/**
 * Two-character key of a logo already stored in the printer's non-volatile
 * memory (YUKI-278, terminals-side YUKI-117).
 *
 * A key, not an image. The raster is uploaded to the printer once, out of
 * band, and every document thereafter names it — so this is authored like the
 * paper width rather than discovered, because nothing on the wire can tell the
 * terminal which keys a given printer actually holds.
 *
 * Exactly two non-space printable ASCII characters for ESC/POS `GS ( L`
 * function 69. The terminal trims strings before rendering, so a space would
 * leave a one-character key (or fall back to the build define). A
 * mistyped key is not an error the printer reports: it prints nothing where
 * the logo should be, and nobody notices until a customer looks at a receipt.
 * Constraining it here at least rejects the lengths that can never work.
 *
 * **Absent is not "no logo" being chosen** — it means nothing was authored,
 * and the resolution chain falls through to the build define. Sending an empty
 * string instead would outrank a lane's `--dart-define` and silently remove a
 * logo that was printing correctly.
 */
exports.ReceiptPrinterLogoKeycode = zod_1.z
    .string()
    .length(2)
    .regex(/^[\x21-\x7e]{2}$/, 'Use exactly two non-space printable ASCII characters');
/**
 * Who drives the receipt printer on this terminal (RL-749).
 *
 * `terminal` — the till opens the printer and prints every document itself.
 * `fiscalService` — a fiscal service on the same machine owns the printer (it
 * prints the signed receipt and its own reports); the till must never open the
 * printer device, even when a port is authored in this block for other
 * reasons, because a printer device has exactly one owner and the till taking
 * it leaves the fiscal service with none.
 *
 * **Absent is a third state, not `terminal`.** Absent keeps what the till has
 * always done: it derives the owner from whether a printer port is configured
 * (a port means the till prints; none means the fiscal service does). So an
 * unauthored fleet does not change, and authoring a port on a lane whose
 * fiscal service prints no longer has to flip the owner silently — say
 * `fiscalService` here.
 */
exports.ReceiptPrinterOwner = zod_1.z.enum(['terminal', 'fiscalService']);
/**
 * The receipt printer's attachment, and how to lay text out on its paper.
 *
 * `columns` is a property of the paper, not the model — the same printer takes
 * 58 mm (32 columns) and 80 mm (48). The terminal cannot read it off the
 * device, so an unauthored or wrong width is invisible until it prints.
 */
exports.ReceiptPrinterConfig = zod_1.z
    .object({
    owner: exports.ReceiptPrinterOwner.optional(),
    transport: zod_1.z.enum(['serial', 'network', 'usb']).optional(),
    serial: exports.SerialPeripheralConfig.optional(),
    network: exports.NetworkPeripheralConfig.optional(),
    columns: zod_1.z.number().int().min(1).max(255).optional(),
    codepage: exports.ReceiptPrinterCodepage.optional(),
    codepageTable: exports.ReceiptPrinterCodepageTable.optional(),
    cut: exports.ReceiptPrinterCut.optional(),
    feedLinesBeforeCut: exports.ReceiptPrinterFeedLinesBeforeCut.optional(),
    font: exports.ReceiptPrinterFont.optional(),
    cashDrawer: zod_1.z.boolean().optional(),
    logoKeycode: exports.ReceiptPrinterLogoKeycode.optional(),
    markingLogoKeycode: exports.ReceiptPrinterLogoKeycode.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.transport === 'network' && value.network?.host === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['network', 'host'],
            message: 'host is required for a network receipt printer',
        });
    }
});
/**
 * The barcode scanner's attachment.
 *
 * A keyboard-wedge scanner needs no port: it types. The serial block is only
 * meaningful for a USB-COM scanner.
 */
/**
 * The kitchen station's label printer (FN-80, ADR-0013 §5): a TSPL printer
 * on a device file (`/dev/usb/lp0`), a USB-serial port or the LAN. The model
 * (dialect and label geometry) is a terminal parameter
 * (`kitchen.label.printerModel`); this block is only where the printer is.
 * Absent means "no label printer": the station prints nothing and says so.
 */
exports.LabelPrinterConfig = zod_1.z
    .object({
    transport: zod_1.z.enum(['serial', 'network', 'usb']).optional(),
    serial: exports.SerialPeripheralConfig.optional(),
    network: exports.NetworkPeripheralConfig.optional(),
})
    .strict()
    .superRefine((value, ctx) => {
    if (value.transport === 'network' && value.network?.host === undefined) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['network', 'host'],
            message: 'host is required for a network label printer',
        });
    }
});
/**
 * Printable ASCII characters outside the GS1 character set 82, so a group
 * separator substitute can never be mistaken for part of a serial or batch
 * value. Mirrors `ScannerGroupSeparator.allowedSubstitutes` on the terminal.
 */
exports.SCANNER_GROUP_SEPARATOR_SUBSTITUTES = '#$@[\\]^`{|}~';
/**
 * What a scanner driver does with the GS1 group separator (ASCII 29) inside a
 * frame (SNX-1220, SNX-EXC-05/09): `discardFrame` — the default, and what an
 * unauthored scanner does — drops the frame; `keep` passes it through;
 * `substitute` replaces it with one allowed character. The terminal reads
 * anything it cannot use as `discardFrame` and never writes the default.
 */
exports.ScannerGroupSeparator = zod_1.z.discriminatedUnion('mode', [
    zod_1.z.object({ mode: zod_1.z.literal('discardFrame') }).strict(),
    zod_1.z.object({ mode: zod_1.z.literal('keep') }).strict(),
    zod_1.z
        .object({
        mode: zod_1.z.literal('substitute'),
        // A pattern rather than a refinement, so the JSON Schema carries it too.
        substitute: zod_1.z
            .string()
            .length(1)
            .regex(/^[#$@[\\\]^`{|}~]$/, 'substitute must be one of #$@[\\]^`{|}~'),
    })
        .strict(),
]);
exports.BarcodeScannerConfig = zod_1.z
    .object({
    transport: zod_1.z.enum(['serial', 'keyboardWedge']).optional(),
    serial: exports.SerialPeripheralConfig.optional(),
    minBarcodeLength: zod_1.z.number().int().min(1).max(255).optional(),
    groupSeparator: exports.ScannerGroupSeparator.optional(),
})
    .strict();
/** MICRO-56 T2: the longest work-directory path a till accepts. */
exports.FISCAL_SECURITY_MODULE_WORK_DIRECTORY_MAX = 512;
/**
 * Whether [path] (already trimmed) is a plain absolute path the till reads:
 * POSIX `/…` with a single leading separator, or a Windows drive `C:\…` /
 * `C:/…`; no `..` segment; no C0/C1 control characters (U+0000–001F,
 * U+007F–009F). Two leading separators in any mix (a UNC share, a device or
 * namespace prefix), a drive-relative `C:work` or a relative path is not. The till's
 * `fiscalWorkDirectoryProblem` (MICRO-56 T1) applies the same rule.
 */
function isPlainAbsolutePath(path) {
    for (let i = 0; i < path.length; i++) {
        const code = path.charCodeAt(i);
        if (code < 0x20 || (code >= 0x7f && code <= 0x9f))
            return false;
    }
    // Two leading separators in any mix (`//`, `\\`, `/\`, `\/`) name a
    // network share or a device namespace (`\\?\`, `//./`) on a Windows till
    // (MICRO-56 T1, c314 F1): refused. A plain POSIX `/…` or a drive remains.
    if (/^[\\/]{2}/.test(path))
        return false;
    if (!(path.startsWith('/') || /^[A-Za-z]:[\\/]/.test(path)))
        return false;
    return !path.split(/[\\/]/).includes('..');
}
/**
 * MICRO-56 T2 — the SoftFiscal work directory the LT security-module lane
 * reads its journal from (agreed with micromarket, 2026-10-27). `path` is
 * trimmed and must be a plain absolute path of at most 512 characters; the
 * till applies the same rule and treats a path it refuses as unset. A block
 * without `path` is "not authored": the till keeps its built-in default.
 */
exports.WorkDirectoryPeripheralConfig = zod_1.z
    .object({
    path: zod_1.z
        .string()
        .trim()
        .min(1)
        .max(exports.FISCAL_SECURITY_MODULE_WORK_DIRECTORY_MAX)
        .refine(isPlainAbsolutePath, 'must be an absolute path without ".." or control characters')
        .optional(),
})
    .strict();
/**
 * Everything Back Office can say about one terminal's hardware.
 *
 * Extending this is additive: a terminal that meets a block it does not know
 * ignores it, and one that meets a missing block falls back. Neither is an
 * error, because a fleet is never all on the same build.
 */
exports.TerminalPeripheralConfig = zod_1.z
    .object({
    receiptPrinter: exports.ReceiptPrinterConfig.optional(),
    barcodeScanner: exports.BarcodeScannerConfig.optional(),
    /** The payment terminal, reached over the network (legacy block). */
    pinPad: exports.NetworkPeripheralConfig.optional(),
    /** The LT security module's serial attachment (legacy block). */
    fiscalSecurityModule: exports.SerialPeripheralConfig.optional(),
    fiscalSecurityModuleEndpoint: exports.ManagedEndpointPeripheralConfig.optional(),
    /** MICRO-56: the SoftFiscal work directory of the LT security-module lane. */
    fiscalSecurityModuleWorkDirectory: exports.WorkDirectoryPeripheralConfig.optional(),
    /** The fiscal sidecar's JSON-RPC endpoint (legacy block). */
    fiscalSidecar: exports.NetworkPeripheralConfig.optional(),
    paymentTerminal: exports.PaymentTerminalConfig.optional(),
    /**
     * The second bank terminal on a till with two (VL-364): Oschadbank, over
     * BPOS-1. `paymentTerminal` stays terminal 1 (PrivatBank). A till reads it
     * only when `payment.bankTerminals.count` is 2.
     */
    secondPaymentTerminal: exports.ManagedEndpointPeripheralConfig.optional(),
    fiscalService: exports.ManagedEndpointPeripheralConfig.optional(),
    /** The SCO bagging-area scale (SSC-116). */
    baggingScale: exports.BaggingScaleConfig.optional(),
    attendantCall: exports.ManagedEndpointPeripheralConfig.optional(),
    authenticationReader: exports.ManagedEndpointPeripheralConfig.optional(),
    lanService: exports.ManagedEndpointPeripheralConfig.optional(),
    /** The kitchen station's label printer (FN-80). */
    labelPrinter: exports.LabelPrinterConfig.optional(),
    /**
     * UVS SoftFiscal for Android (RL-957, owner Q4): the settings BOS hands
     * the APK as its config file's `apk` section. Same fields and checks as
     * that section; every field optional, as in every block.
     */
    softFiscalAndroid: softfiscal_config_js_1.SoftFiscalApkFields.optional(),
})
    .strict();
/**
 * The per-terminal override.
 *
 * `null` is a whole-block tombstone: it restores the terminal's local default
 * even when the group authored that block. Same rule as
 * `TerminalConfigOverrideSchema` — whole-block replacement, never a deep
 * merge, so every delivered value is one a person actually wrote down rather
 * than a half-inherited hybrid nobody authored.
 */
exports.TerminalPeripheralConfigOverride = zod_1.z
    .object({
    receiptPrinter: exports.ReceiptPrinterConfig.nullable().optional(),
    barcodeScanner: exports.BarcodeScannerConfig.nullable().optional(),
    pinPad: exports.NetworkPeripheralConfig.nullable().optional(),
    fiscalSecurityModule: exports.SerialPeripheralConfig.nullable().optional(),
    fiscalSecurityModuleEndpoint: exports.ManagedEndpointPeripheralConfig.nullable().optional(),
    fiscalSecurityModuleWorkDirectory: exports.WorkDirectoryPeripheralConfig.nullable().optional(),
    fiscalSidecar: exports.NetworkPeripheralConfig.nullable().optional(),
    paymentTerminal: exports.PaymentTerminalConfig.nullable().optional(),
    secondPaymentTerminal: exports.ManagedEndpointPeripheralConfig.nullable().optional(),
    fiscalService: exports.ManagedEndpointPeripheralConfig.nullable().optional(),
    baggingScale: exports.BaggingScaleConfig.nullable().optional(),
    attendantCall: exports.ManagedEndpointPeripheralConfig.nullable().optional(),
    authenticationReader: exports.ManagedEndpointPeripheralConfig.nullable().optional(),
    lanService: exports.ManagedEndpointPeripheralConfig.nullable().optional(),
    labelPrinter: exports.LabelPrinterConfig.nullable().optional(),
    softFiscalAndroid: softfiscal_config_js_1.SoftFiscalApkFields.nullable().optional(),
})
    .strict();
/** Every block name, in the order the authoring UI presents them. */
exports.PERIPHERAL_BLOCKS = [
    'receiptPrinter',
    'barcodeScanner',
    'pinPad',
    'fiscalSecurityModule',
    'fiscalSecurityModuleEndpoint',
    'fiscalSecurityModuleWorkDirectory',
    'fiscalSidecar',
    'paymentTerminal',
    'secondPaymentTerminal',
    'fiscalService',
    'baggingScale',
    'attendantCall',
    'authenticationReader',
    'lanService',
    'labelPrinter',
    'softFiscalAndroid',
];
//# sourceMappingURL=terminal-peripheral-config.js.map